import type {FileTreeSelectNode, FileTreeSelectNodeState} from '../components/FileTreeSelect'

export function flattenNodesById(nodes: FileTreeSelectNode[]): Map<string, FileTreeSelectNode> {
  const map = new Map<string, FileTreeSelectNode>()
  const visit = (node: FileTreeSelectNode) => {
    map.set(node.id, node)
    for (const child of node.children ?? []) visit(child)
  }
  for (const node of nodes) visit(node)
  return map
}

/** Returns the node's own path plus every descendant path (files and directories), for cascading select/deselect. */
export function getAllFilePaths(node: FileTreeSelectNode): string[] {
  const out: string[] = []
  const visit = (n: FileTreeSelectNode) => {
    out.push(n.id)
    for (const child of n.children ?? []) visit(child)
  }
  visit(node)
  return out
}

/**
 * Computes every directory's tri-state in one bottom-up pass (each directory aggregates its
 * children's file counts), so a toggle is O(nodes) total instead of O(nodes) per directory row.
 */
export function computeDirectoryStates(
  nodes: FileTreeSelectNode[],
  selected: ReadonlySet<string>,
): Map<string, FileTreeSelectNodeState> {
  const dirStates = new Map<string, FileTreeSelectNodeState>()

  function aggregate(node: FileTreeSelectNode): {selectedFileCount: number; totalFileCount: number} {
    if (node.kind === 'file') {
      return {selectedFileCount: selected.has(node.id) ? 1 : 0, totalFileCount: 1}
    }
    let selectedFileCount = 0
    let totalFileCount = 0
    for (const child of node.children ?? []) {
      const counts = aggregate(child)
      selectedFileCount += counts.selectedFileCount
      totalFileCount += counts.totalFileCount
    }
    const state: FileTreeSelectNodeState =
      totalFileCount === 0 || selectedFileCount === 0
        ? 'unchecked'
        : selectedFileCount === totalFileCount
          ? 'checked'
          : 'indeterminate'
    dirStates.set(node.id, state)
    return {selectedFileCount, totalFileCount}
  }

  for (const node of nodes) aggregate(node)

  return dirStates
}

/**
 * Syncs each ancestor directory's membership in `selected` to its `dirStates` value (present iff
 * fully checked). `dirStates` derives only from file membership, which this never mutates, so one
 * snapshot stays valid for the whole walk.
 */
export function reconcileAncestorDirectories(
  selected: Set<string>,
  path: string,
  dirStates: ReadonlyMap<string, FileTreeSelectNodeState>,
): Set<string> {
  let current = path
  for (;;) {
    const lastSlash = current.lastIndexOf('/')
    if (lastSlash < 0) break
    const parentPath = current.substring(0, lastSlash)
    if (dirStates.get(parentPath) === 'checked') {
      selected.add(parentPath)
    } else {
      selected.delete(parentPath)
    }
    current = parentPath
  }
  return selected
}

/**
 * Re-derives a selection against a rebuilt tree (e.g. after `useRepoFiles` revalidates): keeps only
 * surviving FILE selections and recomputes directory membership from them, so a directory that
 * gained an unselected child on refetch no longer reads (or confirms) as fully checked.
 */
export function reconcileSelectionWithTree(
  prevSelected: ReadonlySet<string>,
  nodeById: ReadonlyMap<string, FileTreeSelectNode>,
  treeNodes: FileTreeSelectNode[],
): {selected: Set<string>; dirStates: Map<string, FileTreeSelectNodeState>} {
  const files = new Set<string>()
  for (const path of prevSelected) {
    if (nodeById.get(path)?.kind === 'file') files.add(path)
  }
  const dirStates = computeDirectoryStates(treeNodes, files)
  const selected = new Set(files)
  for (const [dirPath, state] of dirStates) {
    if (state === 'checked') selected.add(dirPath)
  }
  return {selected, dirStates}
}

export interface FolderizedSelection {
  path: string
  type: 'file' | 'directory'
}

/** Returns the nearest ancestor path of `path` for which `predicate` is true, or undefined. */
function findAncestor(path: string, predicate: (ancestorPath: string) => boolean): string | undefined {
  let current = path
  for (;;) {
    const lastSlash = current.lastIndexOf('/')
    if (lastSlash < 0) return undefined
    const parentPath = current.substring(0, lastSlash)
    if (predicate(parentPath)) return parentPath
    current = parentPath
  }
}

/**
 * Collapses a selection into folder references: a top-level selected folder (not nested under
 * another selected folder) becomes one directory reference once it has at least `maxCount` selected
 * files; otherwise its files, and any file under no selected folder, are emitted individually.
 */
export function folderizeSelection(
  selected: ReadonlySet<string>,
  directories: ReadonlySet<string>,
  maxCount: number,
): FolderizedSelection[] {
  const selectedFolders: string[] = []
  const selectedFiles: string[] = []
  for (const path of selected) {
    if (directories.has(path)) {
      selectedFolders.push(path)
    } else {
      selectedFiles.push(path)
    }
  }
  const selectedFolderSet = new Set(selectedFolders)

  // Top-level = a selected folder with no selected ancestor; a nested one is already covered by that ancestor.
  const topLevelFolders = new Set(
    selectedFolders.filter(folder => findAncestor(folder, ancestor => selectedFolderSet.has(ancestor)) === undefined),
  )

  // Bucket each file under its nearest top-level folder (they never nest, so at most one matches).
  const filesByFolder = new Map<string, string[]>()
  const uncoveredFiles: string[] = []
  for (const file of selectedFiles) {
    const folder = findAncestor(file, ancestor => topLevelFolders.has(ancestor))
    if (folder === undefined) {
      uncoveredFiles.push(file)
      continue
    }
    let bucket = filesByFolder.get(folder)
    if (!bucket) {
      bucket = []
      filesByFolder.set(folder, bucket)
    }
    bucket.push(file)
  }

  const result: FolderizedSelection[] = []
  for (const folder of selectedFolders) {
    if (!topLevelFolders.has(folder)) continue
    const filesInFolder = filesByFolder.get(folder) ?? []
    if (filesInFolder.length >= maxCount) {
      result.push({path: folder, type: 'directory'})
    } else {
      for (const file of filesInFolder) result.push({path: file, type: 'file'})
    }
  }

  for (const file of uncoveredFiles) result.push({path: file, type: 'file'})

  return result
}
